/**
 * Astor Autonomy Levels (UX v3 #1739)
 *
 * Controls how independently the Astor agent platform operates on behalf
 * of a business. Stored as a master value on `businesses.autonomy_level`
 * (the "platform-wide default") and optionally overridden per-agent on
 * `bic_agents.autonomy_level` (NULL = inherit from business master).
 *
 *  - L1 (Observe)    — Astor suggests; the operator acts on every step.
 *  - L2 (Supervised) — Astor recommends; operator approves before each
 *                      action. Platform default for new tenants.
 *  - L3 (Trusted)    — Astor acts within guardrails; escalates on policy
 *                      edges.
 *  - L4 (Full)       — Astor acts autonomously; only escalates exceptions.
 *
 * Values are short string codes so the wire format stays stable across
 * Prisma columns, proto fields, and tRPC payloads.
 */
export enum AstorAutonomyLevel {
  L1 = 'L1',
  L2 = 'L2',
  L3 = 'L3',
  L4 = 'L4',
}

export const ASTOR_AUTONOMY_LEVELS: AstorAutonomyLevel[] = [
  AstorAutonomyLevel.L1,
  AstorAutonomyLevel.L2,
  AstorAutonomyLevel.L3,
  AstorAutonomyLevel.L4,
];

export const DEFAULT_ASTOR_AUTONOMY_LEVEL: AstorAutonomyLevel = AstorAutonomyLevel.L2;

/** Type guard for runtime validation (e.g. JSON payloads from gRPC). */
export function isAstorAutonomyLevel(value: unknown): value is AstorAutonomyLevel {
  return typeof value === 'string' && (ASTOR_AUTONOMY_LEVELS as string[]).includes(value);
}
